/*
**  rui-tabset.js
**
**  rui-tabset component class
*/
import { LitElement, html, css } from 'lit-element';
export class RuiTabset extends LitElement {
    static get is() {
        return 'rui-tabset';
    }
    static get properties() {
        return {
            selectedTabId: { type: String }
        };
    }
    constructor() {
        super();
        this._selectedTabId = '';
        this._tabList = [];
    }
    // Custom setter for selectedTabId property
    set selectedTabId(newValue) {
        if (this._selectedTabId !== newValue) {
            // Store new value
            this._selectedTabId = newValue;
            this.requestUpdate();
            // Is the selected tab already in this tab list?
            let found = false;
            let foundIndex = 0;
            while (foundIndex < this._tabList.length && !found) {
                if (this._tabList[foundIndex].id == this._selectedTabId) {
                    found = true;
                }
                else {
                    foundIndex++;
                }
            }
            // Loop through tabs, then set active one based on selectedTabId
            for (let i = 0; i < this.children.length; i++) {
                if (this.children[i].tagName.toLowerCase() === 'rui-tab') {
                    if (this.children[i]._id == this._selectedTabId) {
                        this.children[i]._isActive = found;
                    }
                    else {
                        this.children[i]._isActive = !found;
                    }
                }
            }
        }
    }
    // Custom getter for selectedTabId property
    get selectedTabId() {
        return this._selectedTabId;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('rui-tab-new', (e) => this.tabNew(e));
        this.addEventListener('rui-tab-updated', (e) => this.tabUpdated(e));
        this.addEventListener('rui-tab-selected', (e) => this.tabSelected(e));
        setTimeout(() => {
            if (this._tabList.length > 0) {
                this.selectedTabId = this._tabList[0].id;
            }
        }, 100);
    }
    disconnectedCallback() {
        this.removeEventListener('rui-tab-new', (e) => this.tabNew(e));
        this.removeEventListener('rui-tab-updated', (e) => this.tabUpdated(e));
        this.removeEventListener('rui-tab-selected', (e) => this.tabSelected(e));
        super.disconnectedCallback();
    }
    tabNew(e) {
        this._tabList.push(e.detail);
    }
    tabUpdated(e) {
        let matchingTabs = this._tabList.filter(tabInfo => tabInfo.id == e.detail.id);
        if (matchingTabs.length < 1) {
            this._tabList.push(e.detail);
        }
        else {
            matchingTabs[0].labelText = e.detail.labelText;
            matchingTabs[0].badge = e.detail.badge;
            matchingTabs[0].isDisabled = e.detail.isDisabled;
            this.requestUpdate();
        }
    }
    tabSelected(e) {
        this.selectedTabId = e.detail;
    }
    tabClick(e) {
        e.preventDefault();
        e.stopPropagation();
        const clickTree = (e.composedPath() || []).filter(item => item.localName === 'button');
        if (clickTree.length > 0) {
            let tabId = clickTree[0].getAttribute('data-rui-tab-id');
            this.selectedTabId = tabId;
        }
    }
    render() {
        return html `
            <div class="rui-tabset-container" role="tablist">
                ${this._tabList.map((tabInfo, index) => html `
                    <button id="rui-tab-label-${tabInfo.id}"
                        class="rui-tab-header ${tabInfo.id == this._selectedTabId ? 'rui-tab-header-active' : ''}"
                        data-rui-tab-id="${tabInfo.id}" ?disabled="${tabInfo.isDisabled}" @click="${this.tabClick}"
                        role="tab" aria-selected="${tabInfo.id == this.selectedTabId}" aria-disabled="${tabInfo.isDisabled}"
                        aria-controls="rui-tab-content-${tabInfo.id}" aria-setsize="${this._tabList.length}" aria-posinset="${index + 1}">
                        ${tabInfo.labelText}
                        ${tabInfo.badge ? html `<span class="rui-tab-header-badge ${tabInfo.isDisabled ? 'rui-tab-header-badge-disabled' : ''}">${tabInfo.badge}</span>` : ''}
                    </button>
                `)}
            </div>
            <div class="rui-tab-content">
                <slot></slot>
            </div>
        `;
    }
    static get styles() {
        return css `
            :host {
                display: block;
            }
            
            .rui-tabset-container {
                height: 46px;
                overflow: visible hidden;
                position: relative;
                white-space: nowrap;
                width: 100%;
                -ms-overflow-style: none; /* for Internet Explorer, Edge */
                scrollbar-width: none; /* for Firefox */
            }
            
            .rui-tabset-container::-webkit-scrollbar {
                display: none; /* for Chrome, Safari, and Opera */
            }
            
            .rui-tab-header {
                background-color: var(--content-primary-bg, #fff);
                box-sizing: border-box;
                border: none;
                border-bottom: 2px solid transparent;
                color: var(--text-regular, #181830);
                cursor: pointer;
                display: inline-block;
                font-family: var(--font-family, "Segoe UI", "Open Sans", "Helvetica Neue", Arial, sans-serif);
                font-size: var(--btn-font-size-md, 14px);
                font-weight: 600;
                height: 100%;
                line-height: var(--btn-line-height-md, 1.42857143);
                margin: 0;
                padding: 12px 18px;
                -webkit-font-smoothing: antialiased;
            }
            
            .rui-tab-header-active {
                border-bottom-color: var(--text-bright, #3c5c72);
                color: var(--text-bright, #3c5c72);
            }
            
            .rui-tab-header:not(:disabled):hover {
                background-color: var( --list-item-hovered-bg, #e5e8eb);
            }
            
            .rui-tab-header:focus {
                background-color: var( --list-item-hovered-bg, #e5e8eb);
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--btn-primary-border, #3878ab);
                outline-offset: -1px;
            }
            
            .rui-tab-header:disabled {
                color: var(--text-disabled, #999);
                cursor: not-allowed;
            }
            
            .rui-tab-header-badge {
                background-color: var(--content-primary-bg, #fff);
                border-color: var(--text-regular, #181830);
                border-radius: var(--btn-badge-border-radius, 10px);
                border-style: var(--btn-badge-border-style, solid);
                border-width: var(--btn-badge-border-width, 1px);
                color: var(--text-regular, #181830);
                display: inline-block;
                font-size: var(--btn-badge-font-size, 12px);
                font-weight: var(--btn-badge-font-weight, 700);
                line-height: var(--btn-badge-line-height, 1);
                margin: -2px 0 0 4px;
                min-width: var(--btn-badge-min-width, 10px);
                opacity: 0.66;
                padding: var(--btn-badge-padding, 3px 6px);
                text-align: center;
                vertical-align: middle;
                white-space: nowrap;
            }
            
            .rui-tab-header-active .rui-tab-header-badge {
                border-color: var(--text-bright, #3c5c72);
                color: var(--text-bright, #3c5c72);
                opacity: 0.9;
            }
            
            .rui-tab-header-badge-disabled {
                color: var(--btn-primary-fg-disabled, #999);
                background-color: var(--btn-primary-bg-disabled, #e9e9e9);
                border-color: var(--btn-primary-border-disabled, #cdcdcd);
            }
            
            .rui-tab-content {
                border: none;
                border-top: 1px solid var(--line, #dbe4eb);
                display: block;
                padding-top: 15px;
            }

            @media print {
                .rui-tab-header {
                    background-color: transparent;
                    color: #000000;
                }    
            
                .rui-tab-header-active {
                    border-bottom-color: #000000;
                }
            }
        `;
    }
}
customElements.get(RuiTabset.is) || customElements.define(RuiTabset.is, RuiTabset);
